<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>

          <div class="section_title">{{ $t('storeMaster.label.title') }}</div>
          <p class="section_desc">{{ $t('storeMaster.message.desc') }}</p>

          <v-alert
            v-if="errorMsg"
            type="error"
            density="compact"
            :text="errorMsg"
            closable
            class="mb-3"
            @click:close="errorMsg = ''"
          />
          <v-alert
            v-if="savedMsg"
            type="success"
            density="compact"
            :text="savedMsg"
            closable
            class="mb-3"
            @click:close="savedMsg = ''"
          />

          <!-- 検索条件・ボタン・ページ送りは、ユーザー登録など他の画面と同じ作りにそろえる（2026-10-26 10/24会議）。
               絞り込みは「検索」を押したときに反映する -->
          <v-row>
            <v-col cols="3">
              <v-text-field
                v-model="searchCondition.storeCode"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :label="$t('storeMaster.label.storeCode')"
                @keydown.enter="search"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="searchCondition.storeName"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :label="$t('storeMaster.label.storeName')"
                @keydown.enter="search"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn theme="dark" class="ml-4 px-8 bg-grey-darken-3" elevation="2"
                :loading="isSearching" :disabled="editingKey != null" @click="search">{{ $t('common.action.search') }}</v-btn>
            </v-col>
          </v-row>
          <!-- 「新規登録」「一括登録」は棚卸日登録・企業コード登録と同じ形・同じ場所（画面右上）。
               一覧の見出しに重ならないよう、一覧との間を空ける（サーバー修正 S-38、2026-10-27 会議） -->
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn dark class="px-6 bg-indigo-darken-1" elevation="2"
                :disabled="editingKey != null || isSaving" @click="addRow">{{ $t('storeMaster.action.register') }}</v-btn>
              <v-btn dark class="ml-4 px-6 bg-indigo-darken-1" elevation="2"
                :disabled="editingKey != null || isSaving" @click="bulkDialogVisible = true">{{ $t('storeMaster.action.bulk') }}</v-btn>
            </v-col>
          </v-row>

          <v-table density="compact" class="mt-4 section_table">
            <thead>
              <tr>
                <th class="nowrap">{{ $t('storeMaster.label.table.no') }}</th>
                <th class="nowrap">{{ $t('storeMaster.label.table.storeCode') }}</th>
                <th class="col_name">{{ $t('storeMaster.label.table.storeName') }}</th>
                <th class="nowrap">{{ $t('storeMaster.label.table.updatedDatetime') }}</th>
                <th class="nowrap">{{ $t('storeMaster.label.table.action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pagedRows" :key="item.row._key" :class="{ editing_row: isEditing(item.row) }">
                <td class="nowrap">{{ item.row._new ? "" : item.no }}</td>

                <!-- 編集中の行だけ入力欄にする（2026-10-23。企業コード登録と同じ作り）。
                     店舗コードも変えられるが、棚卸か担当の店舗ユーザーがある店舗はサーバーが断る -->
                <template v-if="isEditing(item.row)">
                  <td class="code_col">
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      v-model="draft.storeCode"
                      maxlength="5"
                      placeholder="01234"
                      class="code_input"
                      :rules="[checkCode]"
                      @input="draft.storeCode = draft.storeCode.replace(/[^0-9]/g, '')"
                    />
                  </td>
                  <td class="col_name">
                    <v-text-field
                      density="compact" variant="outlined" hide-details="auto"
                      v-model="draft.storeName" maxlength="100"
                      :rules="[checkName]"
                    />
                  </td>
                  <td class="nowrap">{{ formatDatetime(item.row.updatedDatetime) }}</td>
                  <td class="nowrap">
                    <v-btn
                      size="small" variant="outlined" class="mr-2"
                      :disabled="isSaving"
                      @click="cancelEdit"
                    >{{ $t('common.action.cancel') }}</v-btn>
                    <v-btn
                      size="small" variant="flat" color="indigo-darken-1"
                      :loading="isSaving" :disabled="!canFinish"
                      @click="finishEdit"
                    >{{ $t('storeMaster.action.finishEdit') }}</v-btn>
                  </td>
                </template>

                <template v-else>
                  <td class="code_cell nowrap">{{ item.row.storeCode }}</td>
                  <td class="col_name">{{ item.row.storeName }}</td>
                  <td class="nowrap">{{ formatDatetime(item.row.updatedDatetime) }}</td>
                  <td class="nowrap">
                    <v-btn
                      size="small" variant="outlined" color="indigo-darken-1" class="mr-2"
                      :disabled="editingKey != null || isSaving"
                      @click="startEdit(item.row)"
                    >{{ $t('storeMaster.action.edit') }}</v-btn>
                    <v-btn
                      size="small" variant="outlined" color="red-darken-2"
                      :disabled="editingKey != null || isSaving"
                      @click="confirmTarget = item.row"
                    >{{ $t('common.action.delete') }}</v-btn>
                  </td>
                </template>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="5" class="empty_note">{{ $t('storeMaster.message.noStore') }}</td>
              </tr>
              <tr v-else-if="pagedRows.length === 0">
                <td colspan="5">{{ $t('common.message.noData') }}</td>
              </tr>
            </tbody>
          </v-table>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="page" :length="pageCount" :total-visible="9" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select density="compact" variant="outlined" hide-details="auto" v-model="displayPerPage"
                    :items="displayPerPageItem" item-title="name" item-value="value"
                    :label="$t('common.label.displayCountPerPage')" return-object
                    @update:modelValue="page = 1" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayRange.minNo }} - {{ displayRange.maxNo }}{{ $t('common.label.displayedDataRange') }} / {{ filteredRows.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <p class="hint mt-2">{{ $t('storeMaster.message.deleteNote') }}</p>

        </v-container>

        <!-- 削除の確認 -->
        <v-dialog :model-value="confirmTarget != null" width="480" @update:model-value="confirmTarget = null">
          <v-card v-if="confirmTarget">
            <v-card-title><h3><strong>{{ $t('storeMaster.label.deleteTitle') }}</strong></h3></v-card-title>
            <v-card-text>
              {{ $t('storeMaster.message.deleteConfirm', { code: confirmTarget.storeCode, name: confirmTarget.storeName }) }}
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text @click="confirmTarget = null">{{ $t('common.action.cancel') }}</v-btn>
              <v-btn color="red-darken-2" dark :loading="isSaving" @click="remove(confirmTarget)">{{ $t('common.action.delete') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <StoreMasterBulkDialog
          v-if="bulkDialogVisible"
          :handle-cancel="() => (bulkDialogVisible = false)"
          :handle-imported="onImported"
        />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script>
import AppWrapperView from "@/components/views/AppWrapperView";
import StoreMasterBulkDialog from "@/components/dialog/StoreMasterBulkDialog";
import api from "@/apis/staff";
import utils from "@/utils";

/**
 * 店舗登録（Admin のみ。2026-10-21）
 *
 * 良品計画様から店舗マスタは受け取らないため、本部がここで店舗を登録する（D-04）。
 * 店舗は店舗コード（半角数字5桁。DAT の店番と同じ）と店舗名だけを持つ。
 *
 * 一覧は読み取り専用で、行の「編集」を押した行だけを入力欄にする（2026-10-23。企業コード登録と同じ作り）。
 * 登録・店舗名の変更・削除はその場でサーバーに反映する。一度に編集できるのは1行だけ。
 * 店舗コードも「編集」で変えられる（2026-10-23）。ただし店舗コードは棚卸の結果・DAT の店番・
 * ユーザーの担当店舗から参照されるので、それらがある店舗はサーバーが削除・店舗コードの変更を断る。
 *
 * 検索条件（店舗コード・店舗名）・「検索」「新規登録」・ページ送りと表示件数は、
 * ユーザー登録など他の画面と同じ作りにそろえた（2026-10-26 10/24会議）。
 * 「新規登録」は一覧の先頭に空の行を足して編集中にする。
 */
let keySeq = 0;
const withKey = (row) => ({ ...row, _key: ++keySeq });

export default {
  name: "StoreMasterPage",
  components: { AppWrapperView, StoreMasterBulkDialog },

  data() {
    return {
      // 1ページの表示件数（他の画面と同じ選択肢。初期値15件）
      displayPerPage: utils.getDisplayPerPage(this.$t),
      displayPerPageItem: utils.getDisplayPerPageItem(this.$t),
      rows: [],
      page: 1,
      // 入力中の検索条件と、「検索」を押して反映した条件
      searchCondition: { storeCode: "", storeName: "" },
      appliedCondition: { storeCode: "", storeName: "" },
      isSearching: false,
      // 編集中の行（_key）と、その入力中の値。キャンセルしたら捨てる
      editingKey: null,
      draft: null,
      bulkDialogVisible: false,
      confirmTarget: null,
      isSaving: false,
      errorMsg: "",
      savedMsg: "",
    };
  },

  computed: {
    // 1ページの件数。「すべて」（0）は全件
    pageSize() {
      const v = this.displayPerPage?.value ?? 15;
      return v > 0 ? v : Math.max(1, this.filteredRows.length);
    },
    // 「検索」で反映した条件で絞り込んだ行（店舗コードは前方一致、店舗名は部分一致）。
    // No は絞り込み後の通し番号（追加中の行は数えない）。編集中の行は絞り込みで消さない
    filteredRows() {
      const code = (this.appliedCondition.storeCode || "").trim();
      const name = (this.appliedCondition.storeName || "").trim();
      let no = 0;
      return this.rows
        .filter((row) => this.isEditing(row)
          || ((row.storeCode || "").startsWith(code) && (row.storeName || "").includes(name)))
        .map((row) => ({ row, no: row._new ? null : ++no }));
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    // 「x - y件目」の表示
    displayRange() {
      const total = this.filteredRows.length;
      if (total === 0) return { minNo: 0, maxNo: 0 };
      const minNo = (this.page - 1) * this.pageSize + 1;
      return { minNo, maxNo: Math.min(total, this.page * this.pageSize) };
    },
    canFinish() {
      if (this.draft == null || this.isSaving) return false;
      if (this.checkCode(this.draft.storeCode) !== true) return false;
      return this.checkName(this.draft.storeName) === true;
    },
  },

  watch: {
    pageCount(n) {
      if (this.page > n) this.page = n;
    },
  },

  mounted() {
    // 「検索」を押さなくても、開いたときに全店舗を出す
    this.search();
  },

  methods: {
    // 一覧を取り直し、入力中の検索条件で絞り込む
    search() {
      if (this.editingKey != null) return;
      this.isSearching = true;
      api.post("/store/master/list", {}).then((res) => {
        if (res.data.status !== "0") {
          this.errorMsg = res.data.errorMessage || this.$t("common.message.error");
          return;
        }
        this.setRows(res.data.results.storeList);
        this.appliedCondition = { ...this.searchCondition };
        this.page = 1;
      }).finally(() => {
        this.isSearching = false;
      });
    },

    clear() {
      this.searchCondition = { storeCode: "", storeName: "" };
    },

    setRows(list) {
      this.rows = (list || []).map(withKey);
      this.editingKey = null;
      this.draft = null;
    },

    // 一括登録のあとは、サーバーが返した最新の一覧で置き換え、絞り込みを外して先頭のページへ
    onImported(list) {
      this.setRows(list);
      this.clear();
      this.appliedCondition = { storeCode: "", storeName: "" };
      this.page = 1;
      this.errorMsg = "";
      this.savedMsg = "";
    },

    isEditing(row) {
      return this.editingKey != null && row._key === this.editingKey;
    },

    startEdit(row) {
      this.errorMsg = "";
      this.savedMsg = "";
      this.editingKey = row._key;
      this.draft = { storeCode: row.storeCode, storeName: row.storeName || "" };
    },

    // 編集前の値に戻す。追加中の行は消す
    cancelEdit() {
      this.rows = this.rows.filter(r => !(r._new && r._key === this.editingKey));
      this.editingKey = null;
      this.draft = null;
      this.errorMsg = "";
    },

    // 「新規登録」: 先頭に空の行を足して編集中にし、先頭のページへ移る（編集中の行は絞り込みで消えない）
    addRow() {
      this.errorMsg = "";
      this.savedMsg = "";
      const row = withKey({ storeCode: "", storeName: "", updatedDatetime: null, _new: true });
      this.rows.unshift(row);
      this.editingKey = row._key;
      this.draft = { storeCode: "", storeName: "" };
      this.page = 1;
    },

    // 登録済みの店舗でコードを変えていなければ確かめない（5桁になる前に登録された店舗も店舗名を変えられるように）
    checkCode(v) {
      const row = this.rows.find(r => r._key === this.editingKey);
      if (row && !row._new && v === row.storeCode) return true;
      if (!/^[0-9]{5}$/.test(v || "")) return this.$t("storeMaster.validation.storeCode");
      if (this.rows.some(r => !r._new && r.storeCode === v)) return this.$t("storeMaster.validation.exists");
      return true;
    },

    checkName(v) {
      if (!(v || "").trim()) return this.$t("storeMaster.validation.storeName");
      return true;
    },

    // 日本時間で「YYYY-MM-DD HH:mm」（分まで）
    formatDatetime(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const p = (n) => String(n).padStart(2, "0");
      const jst = new Date(d.getTime() + 9 * 3600 * 1000);
      return `${jst.getUTCFullYear()}-${p(jst.getUTCMonth() + 1)}-${p(jst.getUTCDate())} ${p(jst.getUTCHours())}:${p(jst.getUTCMinutes())}`;
    },

    // 登録・変更・削除の共通処理。成功したらサーバーが返した最新の一覧に置き換える
    post(path, body, okMessage) {
      this.errorMsg = "";
      this.savedMsg = "";
      this.isSaving = true;
      return api.post(path, body)
        .then((res) => {
          if (res.data.status !== "0") {
            this.errorMsg = res.data.errorMessage || this.$t("common.message.error");
            return false;
          }
          this.setRows(res.data.results.storeList);
          this.savedMsg = okMessage;
          return true;
        })
        .catch(() => {
          this.errorMsg = this.$t("common.message.unknownError");
          return false;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    finishEdit() {
      if (!this.canFinish) return;
      const row = this.rows.find(r => r._key === this.editingKey);
      const isNew = !!(row && row._new);
      const code = this.draft.storeCode;
      const original = isNew ? null : row.storeCode;
      const name = this.draft.storeName.trim();
      const message = isNew
        ? this.$t("storeMaster.message.added", { code, name })
        : (code !== original
          ? this.$t("storeMaster.message.codeChanged", { from: original, code, name })
          : this.$t("storeMaster.message.renamed", { code, name }));
      this.post("/store/master/save", { storeCode: code, storeName: name, isNew, originalStoreCode: original }, message).then((ok) => {
        if (!ok) return;
        // 保存したあとも、その店舗の行が見えるページへ（絞り込みで見えなければそのまま）
        const idx = this.filteredRows.findIndex(e => e.row.storeCode === code);
        if (idx >= 0) this.page = Math.floor(idx / this.pageSize) + 1;
      });
    },

    remove(row) {
      this.post("/store/master/delete", { storeCode: row.storeCode },
        this.$t("storeMaster.message.deleted", { code: row.storeCode, name: row.storeName }))
        .then(() => {
          this.confirmTarget = null;
        });
    },
  },
};
</script>

<style scoped>
.section_title {
  font-size: 16px;
  font-weight: bold;
  border-left: 4px solid #3949ab;
  padding-left: 8px;
  margin-bottom: 6px;
}
.section_desc {
  font-size: 13px;
  color: #5b6472;
  margin: 0 0 14px;
}
.section_table :deep(thead th) {
  background-color: #3949ab !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  height: 42px !important;
}
.section_table :deep(td) {
  font-size: 14px !important;
  height: 56px !important;
}
.editing_row {
  background-color: #f3f5fc;
}
/* 店舗コードの欄。入力欄の下に出る誤りの文言が途中で切れないよう、幅を取り、文言は折り返す */
.code_col {
  min-width: 180px;
}
.code_input {
  max-width: 200px;
}
.code_input :deep(input),
.code_cell {
  font-family: "Consolas", "Courier New", monospace;
  letter-spacing: 0.08em;
}
.code_input :deep(.v-messages__message) {
  white-space: normal;
  line-height: 1.3;
}
/* 店舗名は残りの幅いっぱいに広がり、長い名前は折り返す */
.col_name {
  width: 100%;
  min-width: 160px;
  word-break: break-all;
}
.nowrap {
  white-space: nowrap;
}
.hint {
  font-size: 12px;
  color: #5b6472;
}
.empty_note {
  color: #c62828;
  font-weight: 700;
}
</style>
